import React, { useEffect, useState } from 'react';
import PropTypes from 'prop-types';
import {
  Button,
  getTranslation,
  Icon,
  Loader,
  TranslationProvider,
} from '@resy/ui-kitchen';
import GreenCheck from '../../../images/icons/check.svg';
import Logger from '../../../../../ui-kitchen/src/utils/logger';

import './PrepayConfirmation.scss';

const testId = 'prepay-start';

function getTiming(prepayData) {
  if (prepayData.timing === 'other') {
    return prepayData.timing_message;
  }
  if (typeof prepayData.timing === 'string') {
    return getTranslation({ translationKey: `prepay_timing_${prepayData.timing}` });
  }
  return null;
}

const prepayConfirmationPropTypes = {
  $routeParams: PropTypes.shape({
    token: PropTypes.string.isRequired,
  }).isRequired,
  Prepay: PropTypes.shape({
    get: PropTypes.func.isRequired,
  }).isRequired,
};

const PrepayConfirmation = ({ $routeParams, Prepay }) => {
  const [prepayData, setPrepayData] = useState({});
  const { token } = $routeParams;

  useEffect(() => {
    const getPrepayData = async () => {
      try {
        const data = await Prepay.get(token);
        setPrepayData(data);
      } catch (error) {
        Logger.error('[PrepayConfirmation] error fetching prepay data: ', error);
      }
    };

    getPrepayData();
  }, [token, Prepay]);

  if (!prepayData?.prepay) {
    return <Loader />;
  }

  const timingFormatted = getTiming(prepayData?.prepay);
  const currencySymbol = prepayData?.venue?.currency_symbol;
  return (
    <div className="PrepayConfirmation">
      <div className="PrepayConfirmation__container">
        <header>
          <div className="PrepayConfirmation__icon-container">
            <Icon iconClass="ResyIcon ResyIcon--confirmed" component={GreenCheck} aria-hidden="true" />
          </div>
          {prepayData.prepay.status === 'charged' ? (
            <>
              <TranslationProvider translationKey="prepay_charged_header">
                <h1 className="PrepayConfirmation__status">Receipt</h1>
              </TranslationProvider>
              <TranslationProvider translationKey="prepay_charged_body">
                <p>
                  Thank you, your card has been charged. If you have any questions, please email
                  {' '}
                  <a href="mailto:support@resy.com?subject=3rd Party Pre-Pay">support@resy.com</a>
                  .
                </p>
              </TranslationProvider>
            </>
          ) : (
            <>
              <TranslationProvider translationKey="prepay_confirmed_header">
                <h1 className="PrepayConfirmation__status">Confirmed</h1>
              </TranslationProvider>
              <TranslationProvider translationKey="prepay_confirmed_body">
                <p>
                  You will receive a receipt via SMS once your card has been charged. If you have any questions, please email
                  {' '}
                  <a href="mailto:support@resy.com?subject=3rd Party Pre-Pay">support@resy.com</a>
                  .
                </p>
              </TranslationProvider>
            </>
          )}
          <hr />
        </header>

        <div className="PrepayConfirmation__form-responses">
          <div className="PrepayConfirmation__line-item">
            <TranslationProvider translationKey="prepay_reservation_name_label">
              <div className="PrepayConfirmation__label">Reservation name</div>
            </TranslationProvider>
            <p>{`${prepayData.reservation.first_name} ${prepayData.reservation.last_name}`}</p>
          </div>
          <div className="PrepayConfirmation__line-item">
            <TranslationProvider translationKey="prepay_summary">
              <div className="PrepayConfirmation__label">Pre-Pay Summary</div>
            </TranslationProvider>
            <p>{prepayData.prepay.description}</p>

            {prepayData.prepay.status === 'charged' ? (
              <p>
                {currencySymbol}
                {prepayData.prepay.charged_amount}
&nbsp;(+
                {prepayData.prepay.gratuity_percentage}
                %)
              </p>
            ) : (
              <p>{`${getTranslation({ translationKey: `prepay_amount_type_label_${prepayData.prepay.amount_type}` })} ${currencySymbol}${prepayData.prepay.amount}`}</p>
            )}
          </div>

          {prepayData.prepay.status !== 'charged' && (
            <div className="PrepayConfirmation__line-item">
              <TranslationProvider translationKey="prepay_is_surprise_label">
                <div className="PrepayConfirmation__label">Is this a surprise?</div>
              </TranslationProvider>
              {prepayData.prepay.is_surprise === 1 ? (
                <TranslationProvider translationKey="yes">
                  <p>Yes</p>
                </TranslationProvider>
              )
                : (
                  <TranslationProvider translationKey="no">
                    <p>No</p>
                  </TranslationProvider>
                )}
            </div>
          )}

          {prepayData.prepay.status !== 'charged' && timingFormatted && (
            <div className="PrepayConfirmation__line-item">
              <TranslationProvider translationKey="prepay_timing_label">
                <div className="PrepayConfirmation__label">When should the guest(s) be informed?</div>
              </TranslationProvider>
              <p>{timingFormatted}</p>
            </div>
          )}
          {prepayData.prepay.message && (
            <div className="PrepayConfirmation__line-item">
              <TranslationProvider translationKey="prepay_message_label">
                <div className="PrepayConfirmation__label">Optional Message</div>
              </TranslationProvider>
              <p>{prepayData.prepay.message}</p>
            </div>
          )}

          <TranslationProvider translationKey="rate_link_to_home">
            <Button
              className="PrepayConfirmation__home-button"
              href="/"
              data-test-id={`${testId}-button-home`}
            >
              Go to the Home Page
            </Button>
          </TranslationProvider>
        </div>
      </div>
    </div>
  );
};

PrepayConfirmation.propTypes = prepayConfirmationPropTypes;

export { getTiming, prepayConfirmationPropTypes };
export default PrepayConfirmation;
